import React, { useEffect, useState } from 'react';
import { X, Calendar, Info, Loader2, AlertTriangle } from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

/**
 * Bulk "Move VAT Claim Period" modal.
 *
 * Inputs:
 *   - documentIds     : array of bill/invoice IDs being moved
 *   - documentType    : 'supplier_bill' | 'customer_invoice'
 *   - currentPeriod   : optional, for display
 *   - onClose / onDone callbacks
 *
 * Fetches the allowed-periods list for the FIRST id (the legal window
 * is identical across docs in a single batch as long as they share
 * the same filing frequency, which is per-tenant).
 */
const fmt = (p) => {
  if (!p) return '';
  if (p.includes('Q')) {
    const [y, q] = p.split('-Q');
    return `الربع ${q} — ${y}`;
  }
  const [y, m] = p.split('-');
  const months = ['', 'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
                  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
  return `${months[parseInt(m, 10)]} ${y}`;
};

export const MoveClaimPeriodModal = ({
  documentIds, documentType, currentPeriod, onClose, onDone,
}) => {
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState([]);
  const [targetPeriod, setTargetPeriod] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [failReason, setFailReason] = useState(null);   // 'network' | 'missing_date' | 'window_exceeded' | null

  useEffect(() => {
    if (!documentIds?.length) return;
    (async () => {
      try {
        setLoading(true);
        setFailReason(null);
        const { data } = await axiosInstance.get('/vat-claims/allowed-periods', {
          params: { document_id: documentIds[0], document_type: documentType },
        });
        const opts = (data.periods || []).filter((p) => p !== data.default_period);
        setPeriods(opts);
        setTargetPeriod(opts[0] || '');
        if (opts.length === 0) {
          if (data.reason?.startsWith('document_missing_natural_date')
              || data.reason?.startsWith('invalid_natural_date')) {
            setFailReason('missing_date');
          } else {
            setFailReason('window_exceeded');
          }
        }
      } catch (e) {
        setFailReason('network');
        toast.error(e.response?.data?.detail || 'تَعَذَّر جَلب الفَترات المَسموحَة');
      } finally {
        setLoading(false);
      }
    })();
  }, [documentIds, documentType]);

  const submit = async () => {
    if (!targetPeriod) {
      toast.error('اختَر فَترة مُستَهدَفة');
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await axiosInstance.post('/vat-claims/defer', {
        document_ids:  documentIds,
        document_type: documentType,
        to_period:     targetPeriod,
        reason:        reason || null,
      });
      const ok = data.deferred?.length || 0;
      const err = data.errors?.length || 0;
      if (ok > 0) toast.success(`تَم تَأجيل ${ok} فاتورة إلى ${fmt(targetPeriod)}`);
      if (err > 0) toast.warning(`فَشَل ${err} فاتورة — راجِع التَفاصيل`);
      onDone?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فَشِل التَّأجيل');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="vat-move-period-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="p-5 bg-gradient-to-r from-amber-600 to-orange-600 text-white flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-7 h-7" />
            <div>
              <h3 className="font-bold text-lg">تَأجيل المُطالَبة الضَّريبية</h3>
              <p className="text-xs opacity-90">
                {documentIds?.length} فاتورة سَتُنقَل إلى إقرار لاحِق
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-white/20 rounded-lg p-1.5"
            data-testid="vat-move-period-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 flex items-start gap-2 text-xs text-blue-900">
            <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            <span>
              لَن يَتَغَيَّر التاريخ المُحاسَبي أو الأستاذ العام. سَيَتِم فَقَط
              تَحويل ضَريبة المَدخَلات لِإقرار الفَترة المُختارَة (وَفق نِظام الزكاة والضَّريبة).
            </span>
          </div>

          {currentPeriod && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">الفَترة الحالِيَّة:</span>
              <span className="font-semibold text-slate-900">{fmt(currentPeriod)}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              نَقل إلى فَترة:
            </label>
            {loading ? (
              <div className="rounded-lg border border-slate-200 p-2 flex items-center gap-2 text-xs text-slate-500">
                <Loader2 className="w-3 h-3 animate-spin" />
                جارٍ الجَلب...
              </div>
            ) : periods.length === 0 ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2 text-xs text-red-900"
                   data-testid="vat-move-period-empty">
                <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <span>
                  {failReason === 'network'
                    && 'فَشَل الاتِّصال بالخادِم عِند جَلب الفَترات. حاوِل مَرَّة أخرى.'}
                  {failReason === 'missing_date'
                    && 'لا يُمكِن نَقل هَذه الفاتورة — تاريخ الفاتورة غَير مُسَجَّل. عَدِّل الفاتورة وأدخِل التاريخ أوَّلاً.'}
                  {failReason === 'window_exceeded'
                    && 'لا تُوجَد فَترات صالِحَة (تَم تَجاوُز النافِذة القانونية 5 سَنوات).'}
                  {!failReason
                    && 'لا تُوجَد فَترات صالِحَة (تَم تَجاوُز النافِذة القانونية 5 سَنوات).'}
                </span>
              </div>
            ) : (
              <select
                value={targetPeriod}
                onChange={(e) => setTargetPeriod(e.target.value)}
                data-testid="vat-move-period-select"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-500"
              >
                {periods.map((p) => (
                  <option key={p} value={p}>
                    {fmt(p)}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              السَّبَب (اختياري):
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="vat-move-period-reason"
              rows={3}
              placeholder="مَثَلاً: لَم تَصِل الفاتورة قَبل تَقديم إقرار الرُّبع السابِق"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="border-t border-slate-200 bg-slate-50 p-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="vat-move-period-cancel"
            className="px-4 py-2 rounded-lg text-sm bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"
          >
            إِلغاء
          </button>
          <button
            onClick={submit}
            disabled={submitting || !targetPeriod || periods.length === 0}
            data-testid="vat-move-period-submit"
            className="px-4 py-2 rounded-lg text-sm bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-semibold disabled:opacity-50"
          >
            {submitting ? '...جارٍ التَّأجيل' : 'تَأكيد التَّأجيل'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveClaimPeriodModal;
